import Lottie from "lottie-react";
import { Calculator } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dimmer, Grid } from "semantic-ui-react";
import {
  useGetIngredientPackaging,
  usePutIngredientDataToQueue
} from "../../../api/ingredients";
import calculatorLoading from "../../../assets/animations/CalculatorLoading.json";
import { CommonModal } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { IngredientDataType, IngredientType } from "../../../config/enums/IngredientType.enum";
import { MappingSourceType } from "../../../config/enums/MappingSourceType.enum";
import { errorView } from "../../../helpers/ErrorHandler";
import IngredientDataRegister from "../ingredientRegister/IngredientDataRegister";
import IngredientPackagingView from "../ingredientRegister/IngredientPackageing";
import SupplerRegister from "../ingredientRegister/SupplierRegister";
import SupplierIngredientLcaDataView, {
  SupplierIngredientLcaDataViewRef
} from "../shared/components/SupplierIngredientLcaDataView";
import TransportationDetails from "../transportationDetails/TransportationDetails";
import "./IngredientEdit.scss";

interface IngredientEditModalProps {
  ingredientIdToUpdate: any;
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  refetchIngredients: () => void;
  isEnableEnvironmentData?: boolean;
  isEnableIngredientMapping?: boolean;
}

interface HandleValidation {
  saveData(documents?: any[]): void;
  setSupplierDocuments?: (documents: any[]) => void;
}

const IngredientEditModal = ({
  ingredientIdToUpdate,
  visibleModal,
  setVisibleModal,
  refetchIngredients,
  isEnableEnvironmentData = false,
  isEnableIngredientMapping = false
}: IngredientEditModalProps) => {
  const ref = useRef<HandleValidation>();
  const lcaRef = useRef<SupplierIngredientLcaDataViewRef>(null);
  const [ingredientData, setIngredientData] = useState<any>(null);
  const [supplierData, setSupplierData] = useState<any>(null);
  const [currentDataType, setCurrentDataType] = useState<any>("SUPPLIER");
  const [viewEnvironmentalData, setViewEnvironmentalData] = useState<boolean>(false);
  const [currentSupplierId, setCurrentSupplierId] = useState<any>(null);
  const [imageData, setImageData] = useState<File | null>(null);
  const [viewPackage, setViewPackage] = useState<any>({
    processIngredient: false,
    produceIngredient: false,
    enableIngredient: false,
    lcaIngredient: false
  });
  const [calculateModalOpen, setCalculateModalOpen] = useState<boolean>(false);
  const [supplierValidation, setSupplierValidation] = useState<any>(false);
  const [isSupplierFormOpen, setIsSupplierFormOpen] = useState<boolean>(false);
  const [validateData, setValidateData] = useState({
    country: 1,
    specifyIngredientValue: "",
    produceType: "",
    ingredientType: ""
  });
  const calculationTile = "Are you sure you have entered all the data for ingredient calculation?";

  const disableCalculateButton = useMemo(
    () => !((isSupplierFormOpen && supplierValidation) || currentSupplierId),
    [currentSupplierId, supplierValidation, isSupplierFormOpen]
  );

  const isGenericCalculationReady = useMemo(() => {
    if (currentDataType !== IngredientDataType.GENERIC || !viewEnvironmentalData) return false;

    if (validateData.ingredientType === IngredientType.RAW) {
      return Boolean(validateData.country && validateData.produceType?.trim());
    }

    if (validateData.ingredientType === IngredientType.PROCESSED) {
      return Boolean(validateData.country && validateData.specifyIngredientValue?.trim());
    }

    return false;
  }, [currentDataType, validateData, viewEnvironmentalData]);

  const genericButtonTitle = isGenericCalculationReady ? "Save and Calculate" : "Save";

  const isIngredientMappingMode = currentDataType === IngredientDataType.USER;
  const navigation = useNavigate();

  useEffect(() => {
    setIngredientData(ingredientIdToUpdate);
    // Set initial supplier ID when editing existing ingredient
    if (ingredientIdToUpdate?.supplier_id) {
      setCurrentSupplierId(ingredientIdToUpdate.supplier_id);
    }
  }, [ingredientIdToUpdate]);

  const packageDataView = viewPackage.enableIngredient ? true : false;
  const transportDataView = viewPackage.processIngredient ? true : false;
  const lcaDataView = viewPackage.lcaIngredient ? true : false;

  let ingredientId = ingredientIdToUpdate?.ingredient_id || null;
  let supplierId = ingredientIdToUpdate?.supplier_id || null;

  let getPackagingData = {
    ingredientId: ingredientId,
    supplierId: supplierId,
    loadPackageData: visibleModal
  };

  const { mutate: putIngredientDataToQueue, isLoading: isLoadingPutDataToQueue } =
    usePutIngredientDataToQueue();

  const calculateHandleApprove = () => {
    putIngredientDataToQueue(
      { ingredientId, supplierId },
      {
        onSuccess: () => {
          setCalculateModalOpen(false);
          refetchIngredients();
          setVisibleModal(false);
        },
        onError: () => {
          setCalculateModalOpen(false);
        }
      }
    );
  };


  useEffect(() => {
    if (ingredientData && ingredientData?.supplierIngredientDetails?.data_type) {
      setViewEnvironmentalData(true);
    }
  }, [ingredientData]);

  const { data: ingredientPackageData, refetch: ingredientPackageDataRefetch } =
    useGetIngredientPackaging(getPackagingData);

  const handleSave = async () => {
    if (!ref?.current) return;

    try {
      // Get uploaded documents
      const uploadedDocuments = lcaRef?.current?.getUploadedDocuments() || [];

      // Pass documents directly to saveData
      await ref.current.saveData(uploadedDocuments);

      // Close modal after successful save
      modalClose();
    } catch (error) {
      console.error("Save error:", error);
      errorView("Save failed");
    }
  };

  const modalClose = () => {
    refetchIngredients();
    setVisibleModal(false);
  };

  const enablePackageData = useMemo(() => {
    return packageDataView && viewEnvironmentalData;
  }, [packageDataView, viewEnvironmentalData]);

  const enableTransportData = useMemo(() => {
    const enableTransportDetails = transportDataView && viewEnvironmentalData;
    return (
      enableTransportDetails ||
      (!enableTransportDetails && packageDataView && viewEnvironmentalData)
    );
  }, [transportDataView, viewEnvironmentalData, packageDataView]);

  const enableIngredientMapping = useMemo(
    () =>
      (currentDataType === IngredientDataType.USER ||
        currentDataType === IngredientDataType.GENERIC) &&
      ingredientId &&
      supplierId &&
      viewEnvironmentalData,
    [currentDataType, viewEnvironmentalData, validateData]
  );

  const enableLCAData = useMemo(() => {
    return lcaDataView && viewEnvironmentalData;
  }, [lcaDataView, viewEnvironmentalData]);

  const handleGenericPrimaryAction = async () => {
    if (!ref?.current) return;

    try {
      // Get uploaded documents
      const uploadedDocuments = lcaRef?.current?.getUploadedDocuments() || [];

      // Pass documents directly to saveData
      await ref.current.saveData(uploadedDocuments);
      if (isGenericCalculationReady) {
        setCalculateModalOpen(true);
      } else {
        // Close modal if not opening calculate modal
        modalClose();
      }
    } catch (error) {
      console.error("Save error:", error);
      errorView("An error occurred while saving the ingredient data. Please try again.");
    }
  };

  const enableIngredientButton = useMemo(
    () =>
      supplierData?.supplier_data?.dataType
        ? !(
            supplierData?.supplier_data?.dataType === IngredientDataType.USER ||
            supplierData?.supplier_data?.dataType === IngredientDataType.GENERIC
          )
        : true,
    [ingredientData, supplierData]
  );

  const checkCurrentSupplier = (supplierId: any) => {
    setCurrentSupplierId(supplierId);
  };

  const navigateIngredientMapping = () =>
    navigation(
      `/mapping/${MappingSourceType.ingredient}?sourceId=${ingredientId}&ownerId=${supplierId}&ingredientName=${ingredientIdToUpdate?.ingredient_name}`
    );

  if (isLoadingPutDataToQueue) {
    return (
      <Dimmer active>
        <div className="loading-calculation">
          <Lottie animationData={calculatorLoading} loop={true} />
        </div>
      </Dimmer>
    );
  }

  return (
    <CommonModal
      modalOpenState={visibleModal}
      modalCloseState={() => {
        modalClose();
      }}
      centered={true}
      title="Edit Ingredient"
    >
      <Grid>
        <Grid.Column computer={16} tablet={16} mobile={16} className="userBankDetailsMain">
          <CommonSplitContainer
            currentHeight={0.92}
            topChild={
              <>
                <IngredientDataRegister
                  ingredientListByProduct={[]}
                  currentIngredientData={ingredientData}
                  ingredientRegisterData={(data: any) => {
                    setIngredientData(data);
                  }}
                  setImageData={setImageData}
                />
                <SupplerRegister
                  ref={ref}
                  lcaRef={lcaRef}
                  onChangeValidateData={setValidateData}
                  ingredientSupplierList={[]}
                  ingredientData={ingredientData}
                  supplierCurrentData={setSupplierData}
                  ingredientList={[]}
                  disableEdit={true}
                  changeDataIngredient={setViewPackage}
                  setViewEnvironmentalData={setViewEnvironmentalData}
                  viewEnvironmentalData={viewEnvironmentalData}
                  currentDataType={setCurrentDataType}
                  isEnableEnvironmentData={isEnableEnvironmentData}
                  checkCurrentSupplier={checkCurrentSupplier}
                  setSupplierValidation={setSupplierValidation}
                  setIsSupplierFormOpen={setIsSupplierFormOpen}
                  closeModal={modalClose}
                />
                {enablePackageData ? (
                  <>
                    <IngredientPackagingView
                      ingredientId={ingredientId}
                      supplierId={supplierId}
                      refetchPackagingData={ingredientPackageDataRefetch}
                      ingredientPackageData={ingredientPackageData}
                    />
                  </>
                ) : null}

                {enableTransportData ? (
                  <TransportationDetails ingredientId={ingredientId} supplierId={supplierId} />
                ) : null}

                {enableLCAData ? (
                  <>
                    <SupplierIngredientLcaDataView
                      ref={lcaRef}
                      ingredientId={ingredientId}
                      supplierId={supplierId}
                    />
                  </>
                ) : null}

                <ConfirmModal
                  viewModal={calculateModalOpen}
                  closeModal={() => {
                    setCalculateModalOpen(false);
                  }}
                  cancel={() => setCalculateModalOpen(false)}
                  approve={calculateHandleApprove}
                  title={"Calculation Ingredient Data"}
                  subTitle={calculationTile}
                  variant="default"
                  confirmText="Calculate"
                  icon={<Calculator className="tw-h-5 tw-w-5" />}
                />
              </>
            }
            bottomChild={
              <MainBottomMultipleButtonView
                leftButtons={[
                  {
                    title: "Cancel",
                    onClick: () => modalClose(),
                    buttonOutLine: true
                  }
                ]}
                rightButtons={
                  isIngredientMappingMode
                    ? [
                        {
                          contentView: 10,
                          title: "Ingredient Mapping",
                          onClick: navigateIngredientMapping,
                          type: "submit",
                          buttonView: enableIngredientMapping,
                          disabled: enableIngredientButton
                        },
                        {
                          contentView: enableIngredientMapping ? 6 : 16,
                          title: "Save",
                          onClick: handleSave,
                          type: "submit",
                          buttonView: true,
                          disabled: disableCalculateButton
                        }
                      ]
                    : [
                        {
                          contentView: 16,
                          title: genericButtonTitle,
                          onClick: handleGenericPrimaryAction,
                          type: "submit",
                          buttonView: true,
                          disabled: disableCalculateButton
                        }
                      ]
                }
              />
            }
          />
        </Grid.Column>
      </Grid>
    </CommonModal>
  );
};

export default IngredientEditModal;
